// ============================================================================
// CraftConverter.jsx
// Interactive "turn my canvas into a craft-ready grid" tool.
// Given the current canvas image + selected craft, pulls the matching color
// library (DMC, Prismacolor, Bullseye, etc.), quantizes the image against
// that palette, and shows a live pixelated preview the user can load back
// onto the canvas or export as a pattern PDF.
// ============================================================================

import { useState, useEffect, useMemo, useCallback } from 'react'
import { X, Grid3X3, Palette, Sparkles, Loader2, Upload, Download } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || ''

// Sensible default grid sizes per craft family
const GRID_PRESETS = {
  cross_stitch: [
    { label: 'Small (60×60)', w: 60, h: 60 },
    { label: 'Medium (100×100)', w: 100, h: 100 },
    { label: 'Large (150×150)', w: 150, h: 150 },
    { label: 'XL (200×200)', w: 200, h: 200 }
  ],
  perler_beads: [
    { label: '29×29 (standard)', w: 29, h: 29 },
    { label: '58×58 (2 boards)', w: 58, h: 58 },
    { label: '100×100 (large)', w: 100, h: 100 }
  ],
  beadwork: [
    { label: 'Loom 40×40', w: 40, h: 40 },
    { label: 'Loom 80×80', w: 80, h: 80 },
    { label: 'Loom 120×120', w: 120, h: 120 }
  ],
  plastic_canvas: [
    { label: '7-mesh 70×70', w: 70, h: 70 },
    { label: '10-mesh 100×100', w: 100, h: 100 }
  ],
  latch_hook: [
    { label: '40×40', w: 40, h: 40 },
    { label: '80×80', w: 80, h: 80 },
    { label: '120×120', w: 120, h: 120 }
  ],
  mosaic: [
    { label: 'Coarse (40×40)', w: 40, h: 40 },
    { label: 'Medium (80×80)', w: 80, h: 80 },
    { label: 'Fine (120×120)', w: 120, h: 120 }
  ],
  pixel_art: [
    { label: '16×16 (icon)', w: 16, h: 16 },
    { label: '32×32', w: 32, h: 32 },
    { label: '64×64', w: 64, h: 64 },
    { label: '128×128', w: 128, h: 128 }
  ],
  diamond_painting: [
    { label: 'Small (100×100)', w: 100, h: 100 },
    { label: 'Medium (200×200)', w: 200, h: 200 },
    { label: 'Large (300×300)', w: 300, h: 300 }
  ],
  paint_by_numbers: [
    { label: 'Simple (60×60)', w: 60, h: 60 },
    { label: 'Detailed (120×120)', w: 120, h: 120 }
  ]
}

const DEFAULT_PRESETS = [
  { label: 'Small (60×60)', w: 60, h: 60 },
  { label: 'Medium (100×100)', w: 100, h: 100 },
  { label: 'Large (160×160)', w: 160, h: 160 }
]

// Map of craft_type key → friendly label shown in the converter header
const CRAFT_LABELS = {
  cross_stitch: 'Cross Stitch',
  hand_embroidery: 'Hand Embroidery',
  machine_embroidery: 'Machine Embroidery',
  knitting_crochet_chart: 'Knit / Crochet Chart',
  amigurumi: 'Amigurumi',
  latch_hook: 'Latch Hook',
  perler_beads: 'Perler / Fuse Beads',
  beadwork: 'Beadwork',
  plastic_canvas: 'Plastic Canvas',
  mosaic: 'Mosaic',
  pixel_art: 'Pixel Art',
  diamond_painting: 'Diamond Painting',
  stained_glass: 'Stained Glass',
  paint_by_numbers: 'Paint by Numbers',
  coloring_page: 'Coloring Page',
  fabric_painting: 'Fabric Painting',
  polymer_clay: 'Polymer Clay',
  candle_soap: 'Candle Making',
  nail_art: 'Nail Art',
  svg_cutting: 'SVG / Cutting',
  vinyl_htv: 'Vinyl HTV',
  sublimation_print: 'Sublimation Print'
}

function labelForCraft(key) {
  return CRAFT_LABELS[key] || (key ? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Craft')
}

// Craft keys available for "view as" override — mostly yarn/grid crafts where
// cross-conversion makes sense (crochet → knit, beadwork → cross-stitch, etc.)
const VIEW_AS_OPTIONS = [
  { key: '', label: 'Match current craft' },
  { key: 'cross_stitch', label: 'Cross stitch (1:1)' },
  { key: 'knitting_crochet_chart', label: 'Knit (5:7 — wider than tall)' },
  { key: 'amigurumi', label: 'Crochet sc (1:1)' },
  { key: 'perler_beads', label: 'Perler / pixel (1:1)' },
  { key: 'beadwork', label: 'Beadwork loom (4:5)' },
  { key: 'latch_hook', label: 'Latch hook (1:1)' },
  { key: 'mosaic', label: 'Mosaic (1:1)' },
  { key: 'diamond_painting', label: 'Diamond (1:1)' }
]

export default function CraftConverter({ isOpen, onClose, canvasImage, craftType, onApplyToCanvas }) {
  const [categories, setCategories] = useState([])       // [{category, label, recommended, brands: [...]}]
  const [activeCategory, setActiveCategory] = useState('')
  const [activeBrand, setActiveBrand] = useState('')
  const [brandPalette, setBrandPalette] = useState([])   // array of full color objects
  const [maxColors, setMaxColors] = useState(24)         // user-selected palette size cap

  const [gridW, setGridW] = useState(100)
  const [gridH, setGridH] = useState(100)
  const [cellPx, setCellPx] = useState(10)
  const [dither, setDither] = useState('floyd')
  const [drawGrid, setDrawGrid] = useState(true)

  // Cell aspect ratio (width:height) from the backend per craft. Controls how
  // the preview renders so knit stitches look wider-than-tall, etc.
  const [cellAspect, setCellAspect] = useState({ w: 1, h: 1, label: 'Square 1:1' })

  // "View as" override — when rendering a crochet design AS a knit chart, etc.
  const [viewAs, setViewAs] = useState('')

  const [preview, setPreview] = useState(null)           // data URL
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showOtherLibs, setShowOtherLibs] = useState(false)

  const effectiveCraft = viewAs || craftType
  const craftLabel = labelForCraft(effectiveCraft)

  const presets = useMemo(() => GRID_PRESETS[effectiveCraft] || DEFAULT_PRESETS, [effectiveCraft])

  // Load the list of relevant library categories when the modal opens or when
  // the effective craft changes (viewAs override or actual craft switch).
  useEffect(() => {
    if (!isOpen) return
    const key = effectiveCraft || 'general'
    fetch(`${API_URL}/colors/for-craft/${encodeURIComponent(key)}`)
      .then(r => r.json())
      .then(data => {
        const cats = data.categories || []
        setCategories(cats)
        if (cats.length > 0) {
          setActiveCategory(cats[0].category)
          const firstBrand = cats[0].brands?.[0]?.id || ''
          setActiveBrand(firstBrand)
        } else {
          setActiveCategory('')
          setActiveBrand('')
        }
        if (data.cellAspect) setCellAspect(data.cellAspect)
      })
      .catch(err => {
        console.error('Failed to load libraries:', err)
        setError('Failed to load color libraries')
      })
  }, [isOpen, effectiveCraft])

  // Load the selected brand's colors whenever brand changes.
  useEffect(() => {
    if (!activeCategory || !activeBrand) {
      setBrandPalette([])
      return
    }
    fetch(`${API_URL}/colors/libraries/${activeCategory}/${activeBrand}`)
      .then(r => r.json())
      .then(data => {
        setBrandPalette(Array.isArray(data.colors) ? data.colors : [])
      })
      .catch(err => {
        console.error('Failed to load brand palette:', err)
        setError('Failed to load brand palette')
      })
  }, [activeCategory, activeBrand])

  // Pick the top-N palette colors (we cap so Kontext/quantizer has a tractable target set).
  const activePalette = useMemo(() => {
    if (!brandPalette.length) return []
    const limit = Math.max(4, Math.min(brandPalette.length, maxColors))
    return brandPalette.slice(0, limit).map(c => c.hex).filter(Boolean)
  }, [brandPalette, maxColors])

  const applyPreset = (preset) => {
    setGridW(preset.w)
    setGridH(preset.h)
    // Auto-scale cell size to keep the preview around ~1000px on the longer side
    const longer = Math.max(preset.w, preset.h)
    const target = 1000
    setCellPx(Math.max(4, Math.min(24, Math.round(target / longer))))
  }

  const runConvert = useCallback(async () => {
    if (!canvasImage) {
      setError('No canvas image to convert. Generate or load a design first.')
      return
    }
    if (activePalette.length < 2) {
      setError('Pick a color library with at least 2 colors.')
      return
    }
    setLoading(true)
    setError('')
    try {
      // Apply craft-specific cell aspect ratio. For a 5:7 knit, use cellPx
      // as the base unit and scale height up relative to width.
      const aspectW = Math.max(1, cellAspect.w || 1)
      const aspectH = Math.max(1, cellAspect.h || 1)
      const base = cellPx
      const cellPxW = Math.max(2, Math.round(base * (aspectW / Math.max(aspectW, aspectH))))
      const cellPxH = Math.max(2, Math.round(base * (aspectH / Math.max(aspectW, aspectH))))
      const body = {
        grid_w: gridW,
        grid_h: gridH,
        cell_px: cellPx,
        cell_px_w: cellPxW,
        cell_px_h: cellPxH,
        palette: activePalette,
        dither,
        draw_grid: drawGrid
      }
      if (canvasImage.startsWith('data:')) {
        body.imageBase64 = canvasImage
      } else {
        body.imageUrl = canvasImage
      }
      const res = await fetch(`${API_URL}/export/convert-craft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Conversion failed')
      setPreview(data.preview)
      setStats(data.stats)
    } catch (err) {
      setError(err.message || 'Conversion failed')
    } finally {
      setLoading(false)
    }
  }, [canvasImage, gridW, gridH, cellPx, activePalette, dither, drawGrid, cellAspect])

  const handleApplyToCanvas = () => {
    if (!preview || !onApplyToCanvas) return
    onApplyToCanvas(preview)
  }

  const handleDownloadPreview = () => {
    if (!preview) return
    const a = document.createElement('a')
    a.href = preview
    a.download = `zenna-${craftType || 'craft'}-${gridW}x${gridH}.png`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  if (!isOpen) return null

  const activeBrandObj = categories.find(c => c.category === activeCategory)?.brands?.find(b => b.id === activeBrand)

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Grid3X3 className="w-5 h-5 text-zenna-violet flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">Convert to {craftLabel}</h2>
              <p className="text-xs text-slate-400 truncate">
                Cell aspect: {cellAspect.label || `${cellAspect.w}:${cellAspect.h}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={viewAs}
              onChange={e => setViewAs(e.target.value)}
              className="hidden sm:block bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs text-slate-200"
              title="View as a different craft (e.g. render a crochet chart with knit aspect ratio)"
            >
              {VIEW_AS_OPTIONS.map(o => (
                <option key={o.key || 'current'} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none">&times;</button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Controls */}
          <div className="w-full md:w-80 flex-shrink-0 border-b md:border-b-0 md:border-r border-slate-700 overflow-y-auto p-4 space-y-5">
            {/* Grid size */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                <Grid3X3 className="w-3.5 h-3.5" /> Grid Size
              </div>
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {presets.map(p => (
                  <button
                    key={p.label}
                    onClick={() => applyPreset(p)}
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      gridW === p.w && gridH === p.h
                        ? 'bg-zenna-violet text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Width</span>
                <input
                  type="number"
                  min={8}
                  max={400}
                  value={gridW}
                  onChange={e => setGridW(Math.max(8, Math.min(400, parseInt(e.target.value, 10) || 0)))}
                  className="w-16 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white"
                />
                <span>×</span>
                <span>Height</span>
                <input
                  type="number"
                  min={8}
                  max={400}
                  value={gridH}
                  onChange={e => setGridH(Math.max(8, Math.min(400, parseInt(e.target.value, 10) || 0)))}
                  className="w-16 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white"
                />
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                {gridW * gridH} total cells
              </div>
            </div>

            {/* Color library — recommended for current craft, with option to browse all */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" /> Color Library
              </div>
              {categories.length === 0 ? (
                <div className="text-xs text-slate-500">No libraries available.</div>
              ) : (
                <>
                  {(() => {
                    const recommended = categories.filter(c => c.recommended)
                    const others = categories.filter(c => !c.recommended)
                    const visibleOthers = showOtherLibs ? others : []
                    return (
                      <>
                        {recommended.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {recommended.map(cat => (
                              <button
                                key={cat.category}
                                onClick={() => {
                                  setActiveCategory(cat.category)
                                  setActiveBrand(cat.brands?.[0]?.id || '')
                                }}
                                className={`px-2 py-1 rounded-full text-[11px] font-medium ${
                                  activeCategory === cat.category
                                    ? 'bg-zenna-violet text-white'
                                    : 'bg-slate-800 text-slate-400 hover:text-white'
                                }`}
                              >
                                {cat.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => setShowOtherLibs(v => !v)}
                          className="text-[11px] text-slate-400 hover:text-white mb-2 underline-offset-2 hover:underline"
                        >
                          {showOtherLibs ? 'Hide other libraries' : `Browse other libraries (${others.length})`}
                        </button>
                        {visibleOthers.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2 pt-1 border-t border-slate-800">
                            {visibleOthers.map(cat => (
                              <button
                                key={cat.category}
                                onClick={() => {
                                  setActiveCategory(cat.category)
                                  setActiveBrand(cat.brands?.[0]?.id || '')
                                }}
                                className={`px-2 py-1 rounded-full text-[11px] font-medium ${
                                  activeCategory === cat.category
                                    ? 'bg-zenna-violet text-white'
                                    : 'bg-slate-800 text-slate-500 hover:text-white'
                                }`}
                              >
                                {cat.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  })()}
                  <select
                    value={activeBrand}
                    onChange={e => setActiveBrand(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {(categories.find(c => c.category === activeCategory)?.brands || []).map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.colorCount})
                      </option>
                    ))}
                  </select>
                  {activeBrandObj?.description && (
                    <div className="mt-1.5 text-[11px] text-slate-500">{activeBrandObj.description}</div>
                  )}
                </>
              )}
            </div>

            {/* Color count cap */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Max Colors</div>
              <input
                type="range"
                min={4}
                max={Math.max(4, brandPalette.length || 24)}
                value={Math.min(maxColors, brandPalette.length || 24)}
                onChange={e => setMaxColors(parseInt(e.target.value, 10))}
                className="w-full accent-zenna-violet"
              />
              <div className="text-xs text-slate-400 mt-1">{activePalette.length} / {brandPalette.length} colors from library</div>
              {brandPalette.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-0.5">
                  {activePalette.slice(0, 64).map((hex, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-sm border border-slate-700"
                      style={{ backgroundColor: hex }}
                      title={hex}
                    />
                  ))}
                  {activePalette.length > 64 && (
                    <span className="text-[10px] text-slate-500 ml-1">+{activePalette.length - 64}</span>
                  )}
                </div>
              )}
            </div>

            {/* Dither + grid overlay */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dither === 'floyd'}
                  onChange={e => setDither(e.target.checked ? 'floyd' : 'none')}
                  className="rounded border-slate-600 bg-slate-800 text-zenna-violet"
                />
                Dithering (smooth gradients)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={drawGrid}
                  onChange={e => setDrawGrid(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-zenna-violet"
                />
                Show grid lines (every 10 heavier)
              </label>
            </div>

            {/* Run button */}
            <button
              onClick={runConvert}
              disabled={loading || !canvasImage || activePalette.length < 2}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-zenna-violet to-zenna-emerald disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Converting…' : 'Convert'}
            </button>
            {error && <div className="text-xs text-red-400">{error}</div>}
          </div>

          {/* Preview */}
          <div className="flex-1 overflow-auto bg-slate-950 p-4">
            {!preview ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm text-center px-6">
                {canvasImage
                  ? 'Configure grid size + color library, then hit Convert to see the pixelated result.'
                  : 'Load or generate a design on the canvas first.'}
              </div>
            ) : (
              <div>
                <img
                  src={preview}
                  alt="Craft conversion preview"
                  className="max-w-full mx-auto border border-slate-700 rounded-lg bg-white"
                />
                {stats && (
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500">Total cells</div>
                      <div className="text-white font-semibold">{stats.grid?.total_cells?.toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500">Colors used</div>
                      <div className="text-white font-semibold">{stats.unique_colors_used} / {stats.palette_size}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500">Dither</div>
                      <div className="text-white font-semibold">{stats.dither === 'floyd' ? 'Floyd–Steinberg' : 'Nearest'}</div>
                    </div>
                  </div>
                )}
                {stats?.colors && stats.colors.length > 0 && (
                  <div className="mt-3 bg-slate-800/40 rounded-lg p-2">
                    <div className="text-[11px] text-slate-400 mb-1.5">Top colors in result</div>
                    <div className="flex flex-wrap gap-1">
                      {stats.colors.slice(0, 24).map(c => (
                        <div key={c.index} className="flex items-center gap-1 bg-slate-800 rounded px-1.5 py-0.5 text-[11px] text-slate-300">
                          <div className="w-3 h-3 rounded-sm border border-slate-600" style={{ backgroundColor: c.hex }} />
                          <span>{c.hex}</span>
                          <span className="text-slate-500">{c.percent}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-700 flex items-center justify-end gap-2">
          {preview && (
            <>
              <button
                onClick={handleDownloadPreview}
                className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" /> Download PNG
              </button>
              {onApplyToCanvas && (
                <button
                  onClick={handleApplyToCanvas}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-white bg-zenna-violet hover:bg-zenna-violet/90 flex items-center gap-1.5"
                >
                  <Upload className="w-4 h-4" /> Apply to Canvas
                </button>
              )}
            </>
          )}
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
